import { useMutation } from '@apollo/client';
import { CreateEmailCustomizationDocument, DeleteOrgEmailCustomizationDocument, SearchEmailCustomizationDocument, UpdateEmailCustomizationDocument, ValidateEmailCustomizationDocument, } from '../generated/graphql-types';
export const useCreateEmailCustomizationMutation = () => {
    const [_createEmailCustomization, { data, error, loading }] = useMutation(CreateEmailCustomizationDocument);
    return {
        data: data?.createEmailCustomization,
        error,
        loading,
        createEmailCustomization: (template_properties, org_identifier) => _createEmailCustomization({
            variables: {
                template_properties,
                org_identifier,
            },
        }),
    };
};
export const useUpdateEmailCustomizationMutation = () => {
    const [_updateEmailCustomization, { data, error, loading }] = useMutation(UpdateEmailCustomizationDocument);
    return {
        data,
        error,
        loading,
        updateEmailCustomization: (template_properties, org_identifier) => _updateEmailCustomization({
            variables: {
                template_properties,
                org_identifier,
            },
        }),
    };
};
export const useSearchEmailCustomizationMutation = () => {
    const [_searchEmailCustomization, { data, error, loading }] = useMutation(SearchEmailCustomizationDocument);
    return {
        data: data?.searchEmailCustomization,
        error,
        loading,
        searchEmailCustomization: (org_identifiers) => _searchEmailCustomization({
            variables: {
                org_identifiers,
            },
        }),
    };
};
export const useDeleteOrgEmailCustomizationMutation = () => {
    const [_deleteOrgEmailCustomization, { data, error, loading },] = useMutation(DeleteOrgEmailCustomizationDocument);
    return {
        data,
        error,
        loading,
        deleteOrgEmailCustomization: (org_identifiers) => _deleteOrgEmailCustomization({
            variables: {
                org_identifiers,
            },
        }),
    };
};
export const useValidateEmailCustomizationMutation = () => {
    const [_validateEmailCustomization, { data, error, loading }] = useMutation(ValidateEmailCustomizationDocument);
    return {
        data,
        error,
        loading,
        validateEmailCustomization: () => _validateEmailCustomization(),
    };
};
export const convertSettingsToTemplateProperties = (settings, baselineSettings) => {
    const result = {};
    const addIfDifferent = (apiKey, settingsKey, transform) => {
        const value = settings[settingsKey];
        const baselineValue = baselineSettings?.[settingsKey];
        if (!baselineSettings || value !== baselineValue) {
            const transformedValue = transform ? transform(value) : value;
            if (transformedValue !== undefined) {
                result[apiKey] = transformedValue;
            }
        }
    };
    addIfDifferent('cta_button_bg_color', 'buttonColor');
    addIfDifferent('cta_text_font_color', 'primaryTextColor');
    addIfDifferent('primary_bg_color', 'cardBackgroundColor');
    if (settings.hideLogoUrl !== 'Hidden') {
        addIfDifferent('logo_url', 'logo', logo => logo !== 'same-as-styling' ? logo : undefined);
    }
    addIfDifferent('hide_logo_url', 'hideLogoUrl', value => value === 'Hidden');
    if (settings.hideProductName !== 'Hidden') {
        addIfDifferent('product_name', 'productName');
    }
    if (settings.hideFooterAddress !== 'Hidden') {
        addIfDifferent('footer_address', 'footerAddress');
    }
    if (settings.hideFooterPhone !== 'Hidden') {
        addIfDifferent('footer_phone', 'mobileNumber');
    }
    if (settings.hidePrivacyPolicy !== 'Hidden') {
        addIfDifferent('company_privacy_policy_url', 'webLinkAddress1');
    }
    addIfDifferent('company_website_url', 'companyWebsiteUrl');
    if (settings.hideContactSupportUrl !== 'Hidden') {
        addIfDifferent('contact_support_url', 'contactSupportUrl');
    }
    addIfDifferent('hide_mobile_app_nudge', 'mobileAppNudge', value => value === 'Hidden');
    addIfDifferent('hide_modify_alert', 'hideModifyAlert', value => value === 'Hidden');
    addIfDifferent('hide_unsubscribe_link', 'hideUnsubscribeLink', value => value === 'Hidden');
    addIfDifferent('hide_manage_notification', 'hideManageNotification', value => value === 'Hidden');
    addIfDifferent('hide_privacy_policy', 'hidePrivacyPolicy', value => value === 'Hidden');
    addIfDifferent('hide_contact_support_url', 'hideContactSupportUrl', value => value === 'Hidden');
    addIfDifferent('hide_notification_status', 'hideNotificationStatus', value => value === 'Hidden');
    addIfDifferent('hide_error_message', 'hideErrorMessage', value => value === 'Hidden');
    addIfDifferent('hide_footer_address', 'hideFooterAddress', value => value === 'Hidden');
    addIfDifferent('hide_footer_phone', 'hideFooterPhone', value => value === 'Hidden');
    addIfDifferent('hide_product_name', 'hideProductName', value => value === 'Hidden');
    addIfDifferent('hide_ts_vocabulary_definitions', 'hide_ts_vocabulary_definitions');
    addIfDifferent('replacement_value_for_answer', 'replacementValueForAnswer', value => value || undefined);
    addIfDifferent('replacement_value_for_liveboard', 'replacementValueForLiveboard', value => value || undefined);
    addIfDifferent('replacement_value_for_spot_iq', 'replacementValueForSpotIQ', value => value || undefined);
    return result;
};
export const convertTemplatePropertiesToSettings = (templateProperties) => {
    return {
        logo: templateProperties.logo_url || 'same-as-styling',
        logoSize: templateProperties.logo_size || 'Medium',
        buttonColor: templateProperties.cta_button_bg_color || '#000000',
        primaryTextColor: templateProperties.cta_text_font_color || '#ffffff',
        cardBackgroundColor: templateProperties.primary_bg_color || '#f4f4f4',
        mobileAppNudge: templateProperties.hide_mobile_app_nudge
            ? 'Hidden'
            : 'Shown',
        hideModifyAlert: templateProperties.hide_modify_alert
            ? 'Hidden'
            : 'Shown',
        hideUnsubscribeLink: templateProperties.hide_unsubscribe_link
            ? 'Hidden'
            : 'Shown',
        hideManageNotification: templateProperties.hide_manage_notification
            ? 'Hidden'
            : 'Shown',
        companySignature: templateProperties.company_signature || '',
        webLinkLabel1: 'Privacy Policy',
        webLinkAddress1: templateProperties.company_privacy_policy_url ||
            'https://www.thoughtspot.com/privacy-statement',
        webLinkLabel2: 'Contact Support',
        contactSupportUrl: templateProperties.contact_support_url ||
            'https://www.thoughtspot.com/support',
        companyWebsiteUrl: templateProperties.company_website_url ||
            'https://www.thoughtspot.com',
        productName: templateProperties.product_name || 'ThoughtSpot',
        footerAddress: templateProperties.footer_address ||
            '444 Castro St, Suite 1000 Mountain View, CA 94041',
        mobileNumber: templateProperties.footer_phone || '(800) 508-7008',
        hide_ts_vocabulary_definitions: templateProperties.hide_ts_vocabulary_definitions || false,
        hideProductName: templateProperties.hide_product_name
            ? 'Hidden'
            : 'Shown',
        hideFooterAddress: templateProperties.hide_footer_address
            ? 'Hidden'
            : 'Shown',
        hideFooterPhone: templateProperties.hide_footer_phone
            ? 'Hidden'
            : 'Shown',
        replacementValueForAnswer: templateProperties.replacement_value_for_answer || 'Answer',
        replacementValueForLiveboard: templateProperties.replacement_value_for_liveboard || 'Liveboard',
        replacementValueForSpotIQ: templateProperties.replacement_value_for_spot_iq || 'SpotIQ',
        hidePrivacyPolicy: templateProperties.hide_privacy_policy === true
            ? 'Hidden'
            : 'Shown',
        hideContactSupportUrl: templateProperties.hide_contact_support_url === true
            ? 'Hidden'
            : 'Shown',
        hideNotificationStatus: templateProperties.hide_notification_status === true
            ? 'Hidden'
            : 'Shown',
        hideErrorMessage: templateProperties.hide_error_message === true ? 'Hidden' : 'Shown',
        hideLogoUrl: templateProperties.hide_logo_url === true ? 'Hidden' : 'Shown',
    };
};
//# sourceMappingURL=email-customization-service.js.map